/**
 * Mobile promo pages: native pages the app opens at `numeo-spot://promo/<slug>` (Figma Spot 10447:108069).
 * Pick a page or create one by slug, edit the draft, then publish.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Input from '@mui/joy/Input'
import List from '@mui/joy/List'
import ListItemButton from '@mui/joy/ListItemButton'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import { isValidPromoPageSlug, promoPageDeeplink } from '@numeo/types'
import type { PromoPageEditorView, PromoPageSummary } from '@numeo/types-main-mobile'
import { apiErrorMessage } from '../../../../api/mobileHomeLayout'
import { mobilePromoPagesApi } from '../../../../api/mobilePromoPages'
import { PromoPageEditor } from './PromoPageEditor'
import { FactoringNoLinkCard } from './FactoringNoLinkCard'

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

export default function MobilePromoPagesPage() {
    const [pages, setPages] = useState<PromoPageSummary[] | null>(null)
    const [listError, setListError] = useState<string | null>(null)
    const [slug, setSlug] = useState<string | null>(null)
    const [view, setView] = useState<PromoPageEditorView | null>(null)
    const [loadingPage, setLoadingPage] = useState(false)
    const [newSlug, setNewSlug] = useState('')
    // The slug the editor shows. A response for any other slug (a failed or overtaken load) must never fill it.
    const selected = useRef<string | null>(null)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    const notify = useCallback((message: string, color: 'success' | 'danger') => setSnackbar({ open: true, message, color }), [])

    const refreshList = useCallback(async () => {
        try {
            setPages(await mobilePromoPagesApi.list())
            setListError(null)
        } catch (err) {
            setListError(apiErrorMessage(err))
        }
    }, [])

    useEffect(() => {
        void refreshList()
    }, [refreshList])

    async function open(next: string) {
        selected.current = next
        setSlug(next)
        setView(null)
        setLoadingPage(true)
        try {
            const loaded = await mobilePromoPagesApi.get(next)
            if (selected.current === next) setView(loaded)
        } catch (err) {
            if (selected.current !== next) return
            setSlug(null)
            notify(apiErrorMessage(err), 'danger')
        } finally {
            if (selected.current === next) setLoadingPage(false)
        }
    }

    const onChanged = (next: PromoPageEditorView) => {
        setView(next)
        void refreshList()
    }

    const slugIsValid = isValidPromoPageSlug(newSlug)
    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h3">Mobile Promo Pages</Typography>
            <Typography level="body-sm" sx={{ mb: 2 }}>
                Native pages the app opens at {promoPageDeeplink('<slug>')}, from a Home banner, a Loads card, the factoring &quot;No&quot; answer or any link.
            </Typography>
            <FactoringNoLinkCard notify={notify} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
                <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Input
                            size="sm"
                            placeholder="new-slug"
                            value={newSlug}
                            onChange={(e) => setNewSlug(e.target.value.trim().toLowerCase())}
                            sx={{ flex: 1 }}
                        />
                        <Button size="sm" disabled={!slugIsValid} onClick={() => void open(newSlug)}>
                            Open
                        </Button>
                    </Stack>
                    {listError ? <Alert color="danger">{listError}</Alert> : null}
                    {pages === null && !listError ? <CircularProgress size="sm" /> : null}
                    <List size="sm">
                        {(pages ?? []).map((p) => (
                            <ListItemButton key={p.slug} selected={p.slug === slug} onClick={() => void open(p.slug)}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography level="title-sm">{p.slug}</Typography>
                                    <Typography level="body-xs" noWrap>
                                        {p.heading}
                                    </Typography>
                                </Box>
                                <Chip size="sm" variant="soft" color={p.isPublished ? 'success' : 'neutral'} sx={{ ml: 'auto' }}>
                                    {p.isPublished ? 'live' : 'draft'}
                                </Chip>
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {loadingPage ? <CircularProgress /> : null}
                    {slug && !loadingPage ? <PromoPageEditor key={slug} slug={slug} view={view} onChanged={onChanged} notify={notify} /> : null}
                    {!slug ? <Typography level="body-md">Pick a page, or type a slug to create one.</Typography> : null}
                </Box>
            </Stack>
            <Snackbar
                open={snackbar.open}
                color={snackbar.color}
                variant="soft"
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
